<script lang="ts">
  /**
   * Mandatory enrolment (the site administrator's second factor).
   *
   * The account that administers a site must carry a second factor, so the server confines its
   * session to enrolment: every other route answers 403 `enrollment_required`. This screen is
   * the only way forward, and it is deliberately not dismissible — the way out is to finish
   * enrolling, or to sign out.
   *
   * It is a separate component from the Settings panel because the situation is different: the
   * user has not chosen this, so it explains why, and it ends by showing the recovery codes
   * before letting them in.
   */
  import qrcode from 'qrcode-generator';
  import { ApiError, api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';

  let secret = $state('');
  let uri = $state('');
  let code = $state('');
  let recovery = $state<string[]>([]);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let copied = $state(false);
  let codeEl = $state<HTMLInputElement | null>(null);

  const qrSvg = $derived(uri ? qrSvgFor(uri) : '');

  $effect(() => {
    if (!uri && !recovery.length) void begin();
  });

  $effect(() => {
    if (uri) codeEl?.focus();
  });

  /** Inline SVG, so nothing is fetched and the CSP stays `default-src 'self'`. */
  function qrSvgFor(text: string): string {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    let path = '';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) path += `M${c} ${r}h1v1h-1z`;
      }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 ${n + 2} ${n + 2}" role="img" aria-label="QR">`
      + `<rect x="-1" y="-1" width="${n + 2}" height="${n + 2}" fill="#fff"/>`
      + `<path d="${path}" fill="#000"/></svg>`;
  }

  async function begin(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaSetup();
      secret = r.secret;
      uri = r.uri;
    } catch (e) {
      // Already enrolled on another device in the meantime: just continue.
      if (e instanceof ApiError && e.code === 'already_enabled') return void store.enrollmentDone();
      error = t('adminFailed');
    } finally {
      busy = false;
    }
  }

  async function confirm(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaConfirm(code.trim());
      recovery = r.recovery;
      secret = '';
      uri = '';
      code = '';
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'bad_code' ? 'twoFactorBadCode' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  async function copyRecovery(): Promise<void> {
    try {
      await navigator.clipboard.writeText(recovery.join('\n'));
      copied = true;
      setTimeout(() => { copied = false; }, 2000);
    } catch {
      /* clipboard blocked: the codes are on screen to copy by hand */
    }
  }
</script>

<div class="wrap">
  <div class="card">
    <img src="/img/logo.svg" alt="" width="48" height="48" />
    <h1>{t('enrollRequiredTitle')}</h1>

    {#if recovery.length}
      <p>{t('twoFactorRecoveryIntro')}</p>
      <ul class="recovery">
        {#each recovery as c (c)}<li>{c}</li>{/each}
      </ul>
      <div class="actions">
        <button type="button" onclick={copyRecovery}>{copied ? t('twoFactorCopied') : t('twoFactorCopy')}</button>
        <button type="button" class="primary" onclick={() => store.enrollmentDone()}>{t('enrollContinue')}</button>
      </div>
    {:else}
      <p class="why">{t('enrollRequiredWhy')}</p>
      <p class="muted">{t('twoFactorScan')}</p>
      {#if qrSvg}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -- locally generated SVG, no user input -->
        <div class="qr">{@html qrSvg}</div>
        <p class="muted">{t('twoFactorManual')}</p>
        <code class="secret" dir="ltr">{secret}</code>
      {/if}
      <label>
        <span>{t('twoFactorCode')}</span>
        <input
          type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code" spellcheck="false"
          maxlength="8" bind:value={code} bind:this={codeEl} />
      </label>
      {#if error}<p class="err" role="alert">{error}</p>{/if}
      <div class="actions">
        <button type="button" class="primary" disabled={busy || code.trim().length < 6} onclick={confirm}>
          {t('twoFactorConfirm')}
        </button>
      </div>
    {/if}

    <button type="button" class="link" onclick={() => void store.logout()}>{t('logout')}</button>
  </div>
</div>

<style>
  /* @css auth/EnrollRequired start */
  .wrap {
    min-height: 100%;
    display: grid;
    place-items: center;
    background: var(--surface-subtle);
    padding: 20px;
  }
  .card {
    width: min(420px, 100%);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 24px;
  }
  .card > img {
    align-self: center;
  }
  h1 {
    margin: 0;
    font-size: 18px;
    text-align: center;
  }
  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
  }
  .why {
    background: var(--surface-subtle);
    border-radius: 8px;
    padding: 8px 10px;
  }
  .muted {
    color: var(--text-muted);
  }
  .qr {
    width: 200px;
    align-self: center;
    background: #fff;
    padding: 8px;
    border-radius: 8px;
  }
  .qr :global(svg) {
    display: block;
    width: 100%;
    height: auto;
  }
  .secret {
    align-self: center;
    letter-spacing: 2px;
    background: var(--surface-subtle);
    padding: 6px 10px;
    border-radius: 6px;
    user-select: all;
  }
  .recovery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .recovery li {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--surface-subtle);
    border-radius: 6px;
    padding: 6px 8px;
    text-align: center;
    direction: ltr;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
  }
  input {
    padding: 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--text);
    font: inherit;
    letter-spacing: 4px;
    text-align: center;
  }
  .actions {
    display: flex;
    gap: 8px;
  }
  .actions button {
    flex: 1;
  }
  .err {
    color: var(--danger, #c0392b);
  }
  .link {
    background: none;
    border: 0;
    color: var(--text-muted);
    font: inherit;
    cursor: pointer;
    padding: 4px;
  }
  /* @css auth/EnrollRequired end */
</style>
